/**
 * Canonical ARKit blendshape vocabulary + per-rig alias resolution.
 *
 * Every facial layer in `figure.ts` (ambient emotion, beat-driven expressions,
 * lip-sync visemes, blink, brow micro-twitch, eye saccade) writes weights
 * keyed by **canonical ARKit camelCase** names — `mouthSmileLeft`, `jawOpen`,
 * `eyeBlinkLeft`, etc. The actual mesh morph names vary by rig:
 *
 *   • Avaturn / RPM / Lewis  — already canonical (`mouthSmileLeft`)
 *   • Unity MSGDI / Asuna    — PascalCase (`MouthSmileLeft`)
 *   • Character Creator      — underscored caps (`Mouth_Smile_L`, `Brow_Down_L`)
 *
 * `buildMorphAliasIndex` is called once at load time. It normalises every rig
 * morph name to a punctuation-stripped lowercase key, then matches against the
 * ARKit set. The result is a bidirectional map:
 *
 *   canonical (`mouthSmileLeft`) → actual (`Mouth_Smile_L`)
 *   actual    (`Mouth_Smile_L`)  → canonical (`mouthSmileLeft`)
 *
 * Callers in `figure.ts` use `actualToCanonical` to resolve the canonical
 * name during the per-frame morph mixer, so a single layer write keyed by
 * canonical (`{ mouthSmileLeft: 0.8 }`) reaches every rig that has the shape
 * regardless of its own naming.
 *
 * `LIP_SYNC_BLENDSHAPES` is the subset of canonical names that the lip-sync
 * layer is allowed to drive. The mixer's `isLipShape` gate uses this so the
 * viseme layer can never accidentally write to brows / eyes / non-mouth
 * shapes if a rig happens to share a key.
 */

/** Standard ARKit 52 blendshape names (canonical camelCase). */
export const ARKIT_BLENDSHAPES = [
  'browDownLeft', 'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight',
  'cheekPuff', 'cheekSquintLeft', 'cheekSquintRight',
  'eyeBlinkLeft', 'eyeBlinkRight', 'eyeLookDownLeft', 'eyeLookDownRight',
  'eyeLookInLeft', 'eyeLookInRight', 'eyeLookOutLeft', 'eyeLookOutRight',
  'eyeLookUpLeft', 'eyeLookUpRight', 'eyeSquintLeft', 'eyeSquintRight',
  'eyeWideLeft', 'eyeWideRight',
  'jawForward', 'jawLeft', 'jawOpen', 'jawRight',
  'mouthClose', 'mouthDimpleLeft', 'mouthDimpleRight', 'mouthFrownLeft', 'mouthFrownRight',
  'mouthFunnel', 'mouthLeft', 'mouthLowerDownLeft', 'mouthLowerDownRight',
  'mouthPressLeft', 'mouthPressRight', 'mouthPucker', 'mouthRight',
  'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthStretchLeft', 'mouthStretchRight',
  'mouthUpperUpLeft', 'mouthUpperUpRight',
  'noseSneerLeft', 'noseSneerRight', 'tongueOut',
] as const;

export type ArkitBlendshape = (typeof ARKIT_BLENDSHAPES)[number];

/**
 * Character-Creator / Reallusion morph names → canonical ARKit keys.
 *
 * CC exports use names like `Eye_Blink_L`, `Mouth_Smile_L`, `Brow_Drop_L` —
 * which case-insensitive normalisation alone can NOT collapse to the ARKit
 * spelling (`eyeblinkleft` vs `eyeblinkl`). This explicit table is applied
 * on top of the normalised match for any rig detected as CC-base.
 *
 * Notes on the trickier mappings:
 *   • `mouthFunnel`     → `Mouth_Contract`         (CC's name for funnel)
 *   • `eyeLookInLeft`   → `Eye_L_Look_R`           (left eye looking right
 *                                                   = convergence, ARKit's
 *                                                   "look in" semantics)
 *   • `browInnerUp`     → `Brow_Raise_Inner_L`     (CC ships only one shape;
 *                                                   we drive that single
 *                                                   shape from canonical
 *                                                   inner-brow lift)
 *   • `cheekPuff`       → `Cheek_Puff_L`           (single shape, both sides)
 */
export const CC_BASE_MORPH_ALIASES: Partial<Record<ArkitBlendshape, string>> = {
  browDownLeft: 'Brow_Drop_L',
  browDownRight: 'Brow_Drop_R',
  browInnerUp: 'Brow_Raise_Inner_L',
  browOuterUpLeft: 'Brow_Raise_Outer_L',
  browOuterUpRight: 'Brow_Raise_Outer_R',
  cheekPuff: 'Cheek_Puff_L',
  cheekSquintLeft: 'Cheek_Raise_L',
  cheekSquintRight: 'Cheek_Raise_R',
  eyeBlinkLeft: 'Eye_Blink_L',
  eyeBlinkRight: 'Eye_Blink_R',
  eyeLookDownLeft: 'Eye_L_Look_Down',
  eyeLookDownRight: 'Eye_R_Look_Down',
  eyeLookInLeft: 'Eye_L_Look_R',
  eyeLookInRight: 'Eye_R_Look_L',
  eyeLookOutLeft: 'Eye_L_Look_L',
  eyeLookOutRight: 'Eye_R_Look_R',
  eyeLookUpLeft: 'Eye_L_Look_Up',
  eyeLookUpRight: 'Eye_R_Look_Up',
  eyeSquintLeft: 'Eye_Squint_L',
  eyeSquintRight: 'Eye_Squint_R',
  eyeWideLeft: 'Eye_Wide_L',
  eyeWideRight: 'Eye_Wide_R',
  jawForward: 'Jaw_Forward',
  jawLeft: 'Jaw_L',
  jawOpen: 'Jaw_Open',
  jawRight: 'Jaw_R',
  mouthClose: 'Mouth_Close',
  mouthDimpleLeft: 'Mouth_Dimple_L',
  mouthDimpleRight: 'Mouth_Dimple_R',
  mouthFrownLeft: 'Mouth_Frown_L',
  mouthFrownRight: 'Mouth_Frown_R',
  mouthFunnel: 'Mouth_Contract',
  mouthLeft: 'Mouth_L',
  mouthLowerDownLeft: 'Mouth_Down_Lower_L',
  mouthLowerDownRight: 'Mouth_Down_Lower_R',
  mouthPressLeft: 'Mouth_Press_L',
  mouthPressRight: 'Mouth_Press_R',
  mouthPucker: 'Mouth_Pucker_Up_L',
  mouthRight: 'Mouth_R',
  mouthRollLower: 'Mouth_Roll_In_Lower_L',
  mouthRollUpper: 'Mouth_Roll_In_Upper_L',
  mouthShrugLower: 'Mouth_Shrug_Lower',
  mouthShrugUpper: 'Mouth_Shrug_Upper',
  mouthSmileLeft: 'Mouth_Smile_L',
  mouthSmileRight: 'Mouth_Smile_R',
  mouthStretchLeft: 'Mouth_Stretch_L',
  mouthStretchRight: 'Mouth_Stretch_R',
  mouthUpperUpLeft: 'Mouth_Up_Upper_L',
  mouthUpperUpRight: 'Mouth_Up_Upper_R',
  noseSneerLeft: 'Nose_Sneer_L',
  noseSneerRight: 'Nose_Sneer_R',
  tongueOut: 'Tongue_Out',
};

/**
 * Subset of ARKit shapes that belong to the lip-sync layer (mouth + jaw +
 * tongue). Brows / eyes / cheeks are deliberately excluded so a viseme write
 * cannot fight a blink or brow twitch on any rig.
 */
export const LIP_SYNC_BLENDSHAPES: ReadonlySet<ArkitBlendshape> = new Set([
  'jawForward', 'jawLeft', 'jawOpen', 'jawRight',
  'mouthClose', 'mouthDimpleLeft', 'mouthDimpleRight', 'mouthFunnel',
  'mouthLeft', 'mouthLowerDownLeft', 'mouthLowerDownRight',
  'mouthPressLeft', 'mouthPressRight', 'mouthPucker', 'mouthRight',
  'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthStretchLeft', 'mouthStretchRight',
  'mouthUpperUpLeft', 'mouthUpperUpRight', 'tongueOut',
]);

export interface MorphAliasIndex {
  /** Canonical ARKit name → actual morph name on this rig (subset, only for shapes the rig has). */
  canonicalToActual: ReadonlyMap<string, string>;
  /** Actual morph name → canonical ARKit name (only for shapes recognised as ARKit). */
  actualToCanonical: ReadonlyMap<string, string>;
  /** True if the rig has an ARKit shape under any naming. */
  hasCanonical: (canonical: string) => boolean;
  /**
   * Resolve a list of canonical candidates to actual mesh morph names that
   * exist on this rig. Used when a feature accepts multiple aliases (e.g.
   * blinks: `eyeBlinkLeft`, `eyeBlinkRight`, `eyesClosed`, `eyeBlink`).
   */
  resolveActual: (...canonicalCandidates: string[]) => string[];
}

export interface MorphAliasOptions {
  /**
   * When set, apply rig-specific morph name tables (e.g. `cc-base`).
   * If unset, the builder auto-detects CC by checking for the canonical
   * presence of `Eye_Blink_L` + `Jaw_Open` (the two shapes that are
   * always-on on CC exports and never used by other pipelines).
   */
  rigProfile?: 'cc-base' | 'cc-base-noprefix' | 'rigify-stretch' | 'avaturn' | 'mixamo' | 'msgdi' | 'metahuman' | 'unity-humanoid' | 'epic-skeleton';
}

function isCcBaseMorphSet(normalizedToActual: ReadonlyMap<string, string>): boolean {
  return normalizedToActual.has(normalizeMorphKey('Eye_Blink_L'))
    && (normalizedToActual.has(normalizeMorphKey('Jaw_Open'))
        || normalizedToActual.has(normalizeMorphKey('V_Open'))
        || normalizedToActual.has(normalizeMorphKey('Mouth_Smile_L')));
}

function applyExplicitMorphAliases(
  aliases: Partial<Record<ArkitBlendshape, string>>,
  normalizedToActual: ReadonlyMap<string, string>,
  canonicalToActual: Map<string, string>,
  actualToCanonical: Map<string, string>,
): void {
  for (const [canonical, ccName] of Object.entries(aliases) as [ArkitBlendshape, string][]) {
    const actual = normalizedToActual.get(normalizeMorphKey(ccName));
    if (!actual) continue;
    canonicalToActual.set(canonical, actual);
    actualToCanonical.set(actual, canonical);
  }
}

/**
 * Strip punctuation + lowercase. Used as the matching key so that
 * `Mouth_Smile_L`, `MouthSmileLeft`, `mouthSmile_L`, `mouthsmileleft` all
 * collapse to a single key (`mouthsmileleft`).
 */
export function normalizeMorphKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function buildMorphAliasIndex(
  morphTargetNames: string[],
  options?: MorphAliasOptions,
): MorphAliasIndex {
  // First pass: collapse rig names by normalised key. If two rig names
  // collapse to the same key (rare), keep the first occurrence.
  const normalizedToActual = new Map<string, string>();
  for (const name of morphTargetNames) {
    const key = normalizeMorphKey(name);
    if (!normalizedToActual.has(key)) {
      normalizedToActual.set(key, name);
    }
  }

  // Second pass: for every canonical ARKit name, see if the rig carries
  // an equivalent shape under any casing.
  const canonicalToActual = new Map<string, string>();
  const actualToCanonical = new Map<string, string>();

  for (const canonical of ARKIT_BLENDSHAPES) {
    const actual = normalizedToActual.get(normalizeMorphKey(canonical));
    if (actual) {
      canonicalToActual.set(canonical, actual);
      actualToCanonical.set(actual, canonical);
    }
  }

  // CC explicit alias pass. Triggered by either an explicit `cc-base*`
  // rigProfile from the caller OR auto-detected by the presence of the
  // CC-specific morph names. CC face shapes use a `_L`/`_R` suffix that
  // case-insensitive normalisation alone cannot collapse to ARKit's
  // `Left`/`Right` words.
  const useCcBase = options?.rigProfile === 'cc-base'
    || options?.rigProfile === 'cc-base-noprefix'
    || (options?.rigProfile == null && isCcBaseMorphSet(normalizedToActual));
  if (useCcBase) {
    applyExplicitMorphAliases(
      CC_BASE_MORPH_ALIASES,
      normalizedToActual,
      canonicalToActual,
      actualToCanonical,
    );
  }

  // Common alternate blink names exported by some pipelines. They aren't
  // strict ARKit but the runtime treats them as left-eye blink shapes.
  for (const [alt, canonical] of [
    ['eyesClosed', 'eyeBlinkLeft'],
    ['eyeBlink', 'eyeBlinkLeft'],
  ] as const) {
    const actual = normalizedToActual.get(normalizeMorphKey(alt));
    if (actual && !actualToCanonical.has(actual)) {
      actualToCanonical.set(actual, canonical);
    }
  }

  return {
    canonicalToActual,
    actualToCanonical,
    hasCanonical: (canonical) => canonicalToActual.has(canonical),
    resolveActual: (...candidates) => {
      const out: string[] = [];
      for (const c of candidates) {
        const actual = canonicalToActual.get(c)
          ?? normalizedToActual.get(normalizeMorphKey(c));
        if (actual && !out.includes(actual)) out.push(actual);
      }
      return out;
    },
  };
}
